import {
  IconButton,
  Badge,
  Box,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  Button,
  VStack,
  Text,
  Divider,
  useDisclosure,
  useToast,
  Portal,
} from "@chakra-ui/react";
import { BellIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { useAlarm } from "./alarmContext";
import { useUser } from "../auth/userContext";
import { fetchNotices } from "../board/notices/api/boardNotices";

const Alarm = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const toast = useToast();

  const alarmCtx = useAlarm();
  const { loginType } = useUser();

  if (!alarmCtx) return null;

  const alarms = alarmCtx.alarms ?? [];
  const unreadCount = alarmCtx.unreadCount ?? 0;
  const markAsRead = alarmCtx.markAsRead;

  const openAlarm = async (alarm) => {
    if (alarm.type === "admin-approval") {
      const monthQuery = /^\d{4}-(0[1-9]|1[0-2])$/.test(alarm.workMonth || "")
        ? `?month=${encodeURIComponent(alarm.workMonth)}`
        : "";
      navigate(`/dashboard/approval${monthQuery}`);
    }
    if (alarm.type === "notice") {
      try {
        const notices = await fetchNotices(
          { loginType, title: alarm.title, page: 1 },
          { toast }
        );
        const matchedNotice = notices.results.find(
          (notice) => notice.title === alarm.title
        );
        navigate(
          matchedNotice?.notice_uuid
            ? `/note/notices/${matchedNotice.notice_uuid}`
            : "/note"
        );
      } catch {
        navigate("/note");
      }
    }
    if (typeof markAsRead === "function") {
      markAsRead(alarm.id);
    }
    onClose();
  };

  return (
    <Popover
      placement="bottom-end"
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      closeOnBlur
    >
      <PopoverTrigger>
        <Box position="relative">
          <IconButton
            icon={<BellIcon />}
            size="sm"
            variant="ghost"
            aria-label="알람"
            fontSize="20px"
            onClick={onOpen}
          />

          {unreadCount > 0 && (
            <Badge
              position="absolute"
              top="-2px"
              right="-2px"
              colorScheme="red"
              borderRadius="full"
              fontSize="0.7em"
              px={2}
            >
              {unreadCount}
            </Badge>
          )}
        </Box>
      </PopoverTrigger>

      <Portal>
      <PopoverContent
        w="320px"
        bg="white"
        opacity={1}
        zIndex={20}
        boxShadow="0 12px 32px rgba(15, 23, 42, 0.18)"
        sx={{
          backgroundColor: "#fff !important",
          opacity: "1 !important",
          isolation: "isolate",
        }}
      >
        <PopoverArrow />

        {/* 🔔 헤더 */}
        <PopoverHeader fontWeight="bold" display="flex">
          🔔 알림 센터

          {/* 🔥 Admin에서만 바로가기 표시 */}
          {loginType === "admin" && (
            <Button
              size="md"
              variant="link"
              colorScheme="blue"
              ml="auto"
              onClick={() => {
                navigate("/dashboard/approval");
                onClose();
              }}
            >
              바로가기
            </Button>
          )}
        </PopoverHeader>

        {/* 🔔 본문 */}
        <PopoverBody bg="white" opacity={1}>
          <VStack
            align="stretch"
            spacing={3}
            maxH="300px"
            overflowY="auto"
          >
            {/* 🔥 요약 */}
            {unreadCount > 0 && (
              <Box
                p={3}
                borderRadius="md"
                bg="red.50"
                border="1px solid"
                borderColor="red.200"
              >
                <Text
                  fontSize="sm"
                  fontWeight="bold"
                  color="red.600"
                >
                  확인할 알림이 {unreadCount}건 있습니다.
                </Text>
              </Box>
            )}

            {/* 🔥 알림 목록 */}
            {alarms.length === 0 ? (
            loginType === "user" ? (
              <Text
                color="gray.500"
                textAlign="center"
                py={6}
              >
                수신된 알림이 없습니다.
              </Text>
            ) : null
          ) : (
            alarms.map((alarm) => (
              <Box
                key={alarm.id}
                p={3}
                borderWidth="1px"
                borderRadius="md"
                cursor="pointer"
                bg={alarm.read ? "white" : "blue.50"}
                borderColor={
                  alarm.read
                    ? "gray.200"
                    : "blue.300"
                }
                _hover={{ bg: "gray.50" }}
                onClick={() => openAlarm(alarm)}
              >
                <Text fontWeight="bold">
                  {alarm.title}
                </Text>

                {alarm.description && (
                  <Text
                    fontSize="sm"
                    color="red.500"
                    mt={1}
                  >
                  {alarm.type === "notice" || alarm.type === "admin-approval"
                    ? alarm.description
                    : `사유: ${alarm.description}`}
                  </Text>
                )}

                <Text
                  fontSize="sm"
                  color="gray.500"
                >
                  {alarm.date} {alarm.time ?? ""}
                </Text>
              </Box>
            ))
          )}
          </VStack>
        </PopoverBody>

        <Divider />

        {/* 🔥 닫기 버튼 */}
        <PopoverFooter textAlign="right" bg="white" opacity={1}>
          <Button size="xs" onClick={onClose}>
            닫기
          </Button>
        </PopoverFooter>
      </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default Alarm;
